import type { SiteControllerFieldsFragment } from 'apollo/generated/client-operations';
import React from 'react';
import {
  reloadFailedOutcome,
  type SiteDeviceOutcome,
} from './siteDeviceResults';

/** Reads the site's devices again and answers them. */
export type ReloadSiteDevices = () => Promise<SiteControllerFieldsFragment[]>;

type RunProps = {
  /** Sends the command and tells its answer, including no answer at all. */
  command: () => Promise<SiteDeviceOutcome>;
  /**
   * Where the answer is written. A toast leaves this drawer's outcome
   * alone, for a command about a different controller.
   */
  showsOn?: 'drawer' | 'toast';
  /** The command applied; the site has been read again (undefined when that read failed). */
  onApplied: (
    outcome: SiteDeviceOutcome,
    controllers: SiteControllerFieldsFragment[] | undefined,
  ) => void;
  /** No answer came; the site has been read again, and says whether the command applied. */
  onLost?: (controllers: SiteControllerFieldsFragment[]) => void;
  /** A non-success outcome was refreshed; callers can explain the current state. */
  onReloaded?: (
    outcome: SiteDeviceOutcome,
    controllers: SiteControllerFieldsFragment[],
  ) => void;
};

export type SiteDeviceCommand = {
  outcome: SiteDeviceOutcome | null;
  setOutcome: (outcome: SiteDeviceOutcome | null) => void;
  /** A command or the reload after it is running: the drawer stays open and its actions off. */
  busy: boolean;
  /** The reload after an answer failed: what is shown may be stale. */
  stale: boolean;
  run: (props: RunProps) => Promise<void>;
  reset: () => void;
};

/**
 * What a drawer that sends site device commands keeps: the last answer,
 * whether a command or the reload after it is running, and whether the state shown may
 * be stale because that read failed. It starts afresh each time the
 * drawer opens, or when `reset` is called, and an answer that arrives
 * after that is dropped rather than shown on the next open.
 */
export function useSiteDeviceCommand({
  open,
  onChanged,
}: {
  open: boolean;
  onChanged: ReloadSiteDevices;
}): SiteDeviceCommand {
  const [outcome, setOutcome] = React.useState<SiteDeviceOutcome | null>(null);
  const [reloading, setReloading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [stale, setStale] = React.useState(false);
  const generation = React.useRef(0);

  const reset = React.useCallback(() => {
    generation.current += 1;
    setOutcome(null);
    setReloading(false);
    setSending(false);
    setStale(false);
  }, []);
  React.useEffect(() => {
    if (open) reset();
  }, [open, reset]);
  // Gone from the screen: an answer that still arrives is dropped.
  React.useEffect(
    () => (): void => {
      generation.current += 1;
    },
    [],
  );

  const reload = async (
    at: number,
  ): Promise<SiteControllerFieldsFragment[] | undefined> => {
    setReloading(true);
    try {
      const controllers = await onChanged();
      if (at === generation.current) setStale(false);
      return controllers;
    } catch {
      if (at === generation.current) {
        setStale(true);
        setOutcome(reloadFailedOutcome());
      }
      return undefined;
    } finally {
      if (at === generation.current) setReloading(false);
    }
  };

  const run = async ({
    command,
    showsOn = 'drawer',
    onApplied,
    onLost,
    onReloaded,
  }: RunProps): Promise<void> => {
    const at = generation.current;
    const onDrawer = showsOn === 'drawer';
    if (onDrawer) setOutcome(null);
    setSending(true);
    let result: SiteDeviceOutcome;
    try {
      result = await command();
    } finally {
      if (at === generation.current) setSending(false);
    }
    if (at !== generation.current) return;
    if (result.type === 'success') {
      const controllers = await reload(at);
      if (at === generation.current) onApplied(result, controllers);
      return;
    }
    if (onDrawer) setOutcome(result);
    else if (!result.reload) onApplied(result, undefined);
    if (!result.reload) return;
    const controllers = await reload(at);
    if (at === generation.current && controllers) {
      const settled = {
        ...result,
        message: result.reloadedMessage ?? result.message,
      };
      if (onDrawer) setOutcome(settled);
      onReloaded?.(settled, controllers);
      if (result.lost) onLost?.(controllers);
    }
  };

  return {
    outcome,
    setOutcome,
    busy: sending || reloading,
    stale,
    run,
    reset,
  };
}
